import { PDFDocument, StandardFonts } from 'pdf-lib';

const BLANK_PDF_URL = '/acord/ACORD_25_blank.pdf';

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

/**
 * The blank "OTHER" rows at the bottom of the coverages table. The ACORD 25
 * provides exactly one such row: a single set of row-level fields (INSR LTR,
 * ADDL INSD, SUBR WVD, policy number, effective/expiration dates and a
 * free-text type-of-insurance description) plus three labelled limit lines.
 * Declared as a list so additional rows extend the capacity automatically.
 */
const OTHER_ROWS = [
  {
    insurerLetter: 'OtherPolicy_InsurerLetterCode_A',
    description: 'OtherPolicy_OtherPolicyDescription_A',
    additionalInsured: 'CertificateOfInsurance_OtherPolicy_AdditionalInsuredCode_A',
    subrogationWaived: 'OtherPolicy_SubrogationWaivedCode_A',
    policyNumber: 'OtherPolicy_PolicyNumberIdentifier_A',
    effectiveDate: 'OtherPolicy_PolicyEffectiveDate_A',
    expirationDate: 'OtherPolicy_PolicyExpirationDate_A',
    limits: [
      { code: 'OtherPolicy_CoverageCode_A', amount: 'OtherPolicy_CoverageLimitAmount_A' },
      { code: 'OtherPolicy_CoverageCode_B', amount: 'OtherPolicy_CoverageLimitAmount_B' },
      { code: 'OtherPolicy_CoverageCode_C', amount: 'OtherPolicy_CoverageLimitAmount_C' },
    ],
  },
];

/** How many excess layers beyond the lead layer this form can carry. */
export const OTHER_ROW_CAPACITY = OTHER_ROWS.length;

/**
 * Thrown when there are more excess layers than the certificate can hold.
 * Carries the unplaced layers so the caller can fall back to an ACORD 101
 * Additional Remarks Schedule rather than issuing a truncated certificate.
 */
export class ExcessCapacityError extends Error {
  constructor(unplaced) {
    const n = unplaced.length;
    super(
      `ACORD 25 cannot hold ${n} additional excess layer${n === 1 ? '' : 's'}: ` +
      `the form provides 1 Umbrella/Excess row plus ${OTHER_ROW_CAPACITY} OTHER ` +
      `row${OTHER_ROW_CAPACITY === 1 ? '' : 's'}. Unplaced: ` +
      unplaced.map(p => `${p.Carrier} ${p.PolicyNumber}`).join('; ') +
      '. Issue the remaining layers on an ACORD 101.'
    );
    this.name = 'ExcessCapacityError';
    this.code = 'EXCESS_CAPACITY_EXCEEDED';
    this.unplacedCount = n;
    this.unplaced = unplaced.map(p => ({
      carrier: p.Carrier,
      policyNumber: p.PolicyNumber,
      layer: p.Layer || '',
    }));
  }
}

/* ------------------------------------------------------------------ *
 * Pure helpers — exported so they can be exercised outside the browser
 * ------------------------------------------------------------------ */

/** Money formatter for limit boxes. The "$" is preprinted on the form. */
export function fmtMoney(v) {
  if (v == null || v === '') return '';
  const n = Number(String(v).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n.toLocaleString('en-US') : '';
}

/** A policy is rendered unless it is explicitly not active. */
export function isActive(p) {
  const s = (p?.Status || '').trim().toLowerCase();
  return s === '' || s === 'active';
}

/**
 * Identity of a carrier for the insurer panel. NAIC code is authoritative —
 * the same carrier is spelled several different ways across policy records
 * (e.g. "STARR SURPLUS LINES INS CO" vs "Starr Surplus Lines Insurance
 * Company", both NAIC 13604). Falls back to a normalized name.
 */
export function carrierKey(p) {
  const naic = String(p?.NAICCode || '').trim();
  if (naic) return `naic:${naic}`;
  return `name:${String(p?.Carrier || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '')}`;
}

/** Which certificate row, if any, a policy belongs to. */
export function rowTypeFor(p) {
  const lob = (p?.LOB || '').toLowerCase();
  if (lob.includes('general liability')) return 'gl';
  if (lob.includes('auto')) return 'auto';
  if (lob.includes('umbrella') || lob.includes('excess')) return 'excess';
  if (lob.includes('workers')) return 'wc';
  return null;
}

/**
 * Attachment point of an excess layer, parsed from the `Layer` field
 * (e.g. "$5M xs $1M" -> 1000000). Used to order excess rows lead-layer-first.
 * Unknown layers sort last, keeping their original relative order.
 */
export function attachmentOf(p) {
  const m = String(p?.Layer || '').match(/xs\s*\$?\s*([\d.]+)\s*([mkMK]?)/);
  if (!m) return Number.POSITIVE_INFINITY;
  const mult = m[2].toLowerCase() === 'm' ? 1e6 : m[2].toLowerCase() === 'k' ? 1e3 : 1;
  return Number(m[1]) * mult;
}

/**
 * Select the policies that actually appear as rows on the certificate and
 * assign insurer letters in the order those rows appear on the form.
 *
 * Property, inland marine, stock throughput, EPL, foreign liability etc. are
 * deliberately excluded — they have no row on an ACORD 25, so their carriers
 * must not occupy a slot in the insurer panel.
 */
export function selectCertificateRows(policies) {
  const active = (policies || []).filter(isActive);

  const gl = active.find(p => rowTypeFor(p) === 'gl') || null;
  const auto = active.find(p => rowTypeFor(p) === 'auto') || null;
  const wc = active.find(p => rowTypeFor(p) === 'wc') || null;
  const excess = active
    .filter(p => rowTypeFor(p) === 'excess')
    .map((p, i) => ({ p, i }))
    .sort((a, b) => (attachmentOf(a.p) - attachmentOf(b.p)) || (a.i - b.i))
    .map(x => x.p);

  // Order of appearance on the form: GL, Auto, Excess/Umbrella (in order), WC.
  const ordered = [gl, auto, ...excess, wc].filter(Boolean);

  const carriers = [];
  const byKey = new Map();
  for (const p of ordered) {
    const key = carrierKey(p);
    if (byKey.has(key)) {
      // Same carrier under a different spelling — prefer the fully spelled
      // out legal name for the panel.
      const entry = byKey.get(key);
      const name = String(p.Carrier || '');
      if (name.length > entry.name.length) entry.name = name;
      continue;
    }
    if (carriers.length >= LETTERS.length) continue;
    const entry = {
      key,
      name: String(p.Carrier || ''),
      naic: String(p.NAICCode || ''),
      letter: LETTERS[carriers.length],
    };
    byKey.set(key, entry);
    carriers.push(entry);
  }

  const letterFor = (p) => (p ? byKey.get(carrierKey(p))?.letter || '' : '');

  return { gl, auto, excess, wc, carriers, letterFor };
}

/**
 * Derive the covered-autos checkboxes from the policy record.
 *
 * NOTE: FIG_Policies has no column describing auto coverage scope. The scope
 * must come from an explicit `CoverageScope` field — it is never inferred
 * from the policy title or premium, because marking the wrong box on a
 * certificate misrepresents coverage. When the field is absent, every scope
 * box is left blank rather than defaulting to ANY AUTO.
 */
export function deriveAutoScope(policy) {
  const boxes = {
    any: false,
    ownedOnly: false,
    scheduled: false,
    hiredOnly: false,
    nonOwnedOnly: false,
  };
  const raw = String(policy?.CoverageScope || '').trim();
  if (!raw) return { boxes, resolved: false };

  const s = raw.toLowerCase();
  if (s.includes('any')) {
    boxes.any = true;
  } else {
    if (/\bhired\b/.test(s)) boxes.hiredOnly = true;
    if (/non[-\s]?owned/.test(s) || /\bhnoa\b/.test(s)) boxes.nonOwnedOnly = true;
    if (/\bhnoa\b/.test(s)) boxes.hiredOnly = true;
    if (/\bscheduled\b/.test(s)) boxes.scheduled = true;
    // "owned" on its own — must not be triggered by "non-owned"
    if (/(^|[^-\s])\bowned\b/.test(s.replace(/non[-\s]?owned/g, ''))) boxes.ownedOnly = true;
  }
  return { boxes, resolved: Object.values(boxes).some(Boolean) };
}

/**
 * "ANY PROPRIETOR/PARTNER/EXECUTIVE OFFICER/MEMBER EXCLUDED?" on the WC row.
 *
 * Y when any officer is excluded from coverage, N when officers are on record
 * and all are included. With no officer records — or records whose WCIncluded
 * is neither true nor false — the answer is unknown, so the box is left blank
 * and a warning is raised. Answering this wrong misstates who is covered.
 *
 * @returns {{value: 'Y'|'N'|'', reason: string}}
 */
export function deriveOfficerExclusion(officers) {
  const list = officers || [];
  if (list.length === 0) {
    return { value: '', reason: 'no FIG_Officers records for this insured' };
  }
  if (list.some(o => o.WCIncluded === false)) {
    const names = list.filter(o => o.WCIncluded === false).map(o => o.OfficerName || o.Title).join(', ');
    return { value: 'Y', reason: `excluded from WC: ${names}` };
  }
  if (list.every(o => o.WCIncluded === true)) {
    return { value: 'N', reason: `all ${list.length} officer record(s) have WCIncluded = true` };
  }
  const indeterminate = list.filter(o => o.WCIncluded !== true && o.WCIncluded !== false);
  return {
    value: '',
    reason: `WCIncluded is unset on ${indeterminate.length} of ${list.length} officer record(s): ` +
      indeterminate.map(o => o.OfficerName || `id ${o.id}`).join(', '),
  };
}

/** SUBR WVD column: Y when the policy carries a waiver of subrogation. */
export function subrCode(policy) {
  const v = String(policy?.SubrogationWaiver ?? '').trim().toLowerCase();
  if (['yes', 'y', 'true', '1'].includes(v)) return 'Y';
  return 'N';
}

/* ------------------------------------------------------------------ *
 * PDF filling
 * ------------------------------------------------------------------ */

/**
 * Fill the certificate.
 * @param {Uint8Array|ArrayBuffer} pdfBytes - blank ACORD 25
 * @param {Object} ctx - insured/agency/certHolder/rows/today/warnings
 */
async function fillCertificate(pdfBytes, ctx) {
  const { insured, agency, certHolder, rows, officers, today, warnings } = ctx;

  const pdfDoc = await PDFDocument.load(pdfBytes);
  const form = pdfDoc.getForm();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

  const rendered = {};

  function setText(name, value) {
    if (value === undefined || value === null || value === '') return;
    try {
      const field = form.getTextField(name);
      field.setText(String(value));
      try { field.updateAppearances(font); } catch { /* appearance regenerated on save */ }
      rendered[name] = String(value);
    } catch (err) {
      warnings.push(`Text field not found: "${name}" (${err.message})`);
    }
  }

  /**
   * Write into one of the narrow OTHER-row limit boxes.
   *
   * Those boxes are flagged multiline in the template even though they are a
   * single 12pt-tall line; pdf-lib top-aligns multiline text and pushes the
   * glyphs below the box, where the appearance stream clips them. They are
   * also narrow enough that an 8pt label overflows ("EACH OCCURRENCE" is
   * 81.5pt against a 79pt box). So: force single-line and shrink to fit.
   * This adjusts the generated document only — the blank template is untouched.
   */
  function setTextFitted(name, value, maxSize = 8) {
    if (value === undefined || value === null || value === '') return;
    try {
      const field = form.getTextField(name);
      if (field.isMultiline()) field.disableMultiline();

      const width = field.acroField.getWidgets()[0].getRectangle().width - 3;
      let size = maxSize;
      while (size > 5 && font.widthOfTextAtSize(String(value), size) > width) size -= 0.25;

      field.setFontSize(size);
      field.setText(String(value));
      try { field.updateAppearances(font); } catch { /* appearance regenerated on save */ }
      rendered[name] = String(value);
    } catch (err) {
      warnings.push(`Text field not found: "${name}" (${err.message})`);
    }
  }

  function setCheck(name, on = true) {
    if (!on) return;
    try {
      form.getCheckBox(name).check();
      rendered[name] = '[X]';
    } catch (err) {
      warnings.push(`Checkbox not found: "${name}" (${err.message})`);
    }
  }

  // --- Producer ---
  setText('Producer_FullName_A', agency?.AgencyName);
  setText('Producer_MailingAddress_LineOne_A', agency?.AgencyAddress);
  setText('Producer_MailingAddress_CityName_A', agency?.AgencyCity);
  setText('Producer_MailingAddress_StateOrProvinceCode_A', agency?.AgencyState);
  setText('Producer_MailingAddress_PostalCode_A', agency?.AgencyZip);
  setText('Producer_ContactPerson_FullName_A', agency?.ContactName);
  setText('Producer_ContactPerson_PhoneNumber_A', agency?.ContactPhone);
  setText('Producer_FaxNumber_A', agency?.ContactFax);
  setText('Producer_ContactPerson_EmailAddress_A', agency?.ContactEmail);

  // --- Insured ---
  setText('NamedInsured_FullName_A', insured?.CompanyName);
  setText('NamedInsured_MailingAddress_LineOne_A', insured?.MailingAddress);
  setText('NamedInsured_MailingAddress_CityName_A', insured?.MailingCity);
  setText('NamedInsured_MailingAddress_StateOrProvinceCode_A', insured?.MailingState);
  setText('NamedInsured_MailingAddress_PostalCode_A', insured?.MailingZip);

  // --- Insurer panel (only carriers with a row on this certificate) ---
  for (const c of rows.carriers) {
    setText(`Insurer_FullName_${c.letter}`, c.name);
    setText(`Insurer_NAICCode_${c.letter}`, c.naic);
  }

  // --- General Liability ---
  if (rows.gl) {
    const gl = rows.gl;
    setText('GeneralLiability_InsurerLetterCode_A', rows.letterFor(gl));
    setCheck('GeneralLiability_OccurrenceIndicator_A');
    setText('Policy_GeneralLiability_PolicyNumberIdentifier_A', gl.PolicyNumber);
    setText('Policy_GeneralLiability_EffectiveDate_A', gl.EffectiveDate);
    setText('Policy_GeneralLiability_ExpirationDate_A', gl.ExpirationDate);

    if (certHolder?.IsAdditionalInsured && (certHolder.AI_LOBs || '').toLowerCase().includes('gl')) {
      setText('CertificateOfInsurance_GeneralLiability_AdditionalInsuredCode_A', 'Y');
    }
    setText('Policy_GeneralLiability_SubrogationWaivedCode_A', subrCode(gl));

    setText('GeneralLiability_EachOccurrence_LimitAmount_A', fmtMoney(gl.EachOccurrenceLimit));
    setText('GeneralLiability_FireDamageRentedPremises_EachOccurrenceLimitAmount_A', fmtMoney(gl.DamageToRentedPremises));
    setText('GeneralLiability_MedicalExpense_EachPersonLimitAmount_A', fmtMoney(gl.MedicalExpenseLimit ?? gl.MedExpenseLimit));
    setText('GeneralLiability_PersonalAndAdvertisingInjury_LimitAmount_A', fmtMoney(gl.PersonalAdvInjuryLimit));
    setText('GeneralLiability_GeneralAggregate_LimitAmount_A', fmtMoney(gl.GeneralAggregateLimit));
    setText('GeneralLiability_ProductsAndCompletedOperations_AggregateLimitAmount_A', fmtMoney(gl.ProductsCompOpsAggLimit));
  }

  // --- Automobile Liability ---
  if (rows.auto) {
    const auto = rows.auto;
    setText('Vehicle_InsurerLetterCode_A', rows.letterFor(auto));

    const { boxes, resolved } = deriveAutoScope(auto);
    if (!resolved) {
      warnings.push(
        `Auto policy ${auto.PolicyNumber}: no CoverageScope value — covered-autos ` +
        `checkboxes left blank (ANY AUTO deliberately not defaulted).`
      );
    }
    setCheck('Vehicle_AnyAutoIndicator_A', boxes.any);
    setCheck('Vehicle_AllOwnedAutosIndicator_A', boxes.ownedOnly);
    setCheck('Vehicle_ScheduledAutosIndicator_A', boxes.scheduled);
    setCheck('Vehicle_HiredAutosIndicator_A', boxes.hiredOnly);
    setCheck('Vehicle_NonOwnedAutosIndicator_A', boxes.nonOwnedOnly);

    setText('Policy_AutomobileLiability_PolicyNumberIdentifier_A', auto.PolicyNumber);
    setText('Policy_AutomobileLiability_EffectiveDate_A', auto.EffectiveDate);
    setText('Policy_AutomobileLiability_ExpirationDate_A', auto.ExpirationDate);

    if (certHolder?.IsAdditionalInsured && (certHolder.AI_LOBs || '').toLowerCase().includes('auto')) {
      setText('CertificateOfInsurance_AutomobileLiability_AdditionalInsuredCode_A', 'Y');
    }
    setText('Policy_AutomobileLiability_SubrogationWaivedCode_A', subrCode(auto));

    setText('Vehicle_CombinedSingleLimit_EachAccidentAmount_A', fmtMoney(auto.EachOccurrenceLimit));
  }

  // --- Excess / Umbrella (lead layer) ---
  const [leadExcess, ...additionalExcess] = rows.excess;
  if (leadExcess) {
    const xs = leadExcess;
    setText('ExcessUmbrella_InsurerLetterCode_A', rows.letterFor(xs));

    if ((xs.LOB || '').toLowerCase().includes('umbrella')) {
      setCheck('Policy_PolicyType_UmbrellaIndicator_A');
    } else {
      setCheck('Policy_PolicyType_ExcessIndicator_A');
    }
    setCheck('ExcessUmbrella_OccurrenceIndicator_A');

    setText('Policy_ExcessLiability_PolicyNumberIdentifier_A', xs.PolicyNumber);
    setText('Policy_ExcessLiability_EffectiveDate_A', xs.EffectiveDate);
    setText('Policy_ExcessLiability_ExpirationDate_A', xs.ExpirationDate);

    if (certHolder?.IsAdditionalInsured && (certHolder.AI_LOBs || '').toLowerCase().match(/umb|excess/)) {
      setText('CertificateOfInsurance_ExcessLiability_AdditionalInsuredCode_A', 'Y');
    }
    setText('Policy_ExcessLiability_SubrogationWaivedCode_A', subrCode(xs));

    setText('ExcessUmbrella_Umbrella_EachOccurrenceAmount_A', fmtMoney(xs.EachOccurrenceLimit));
    setText('ExcessUmbrella_Umbrella_AggregateAmount_A', fmtMoney(xs.GeneralAggregateLimit));
  }

  // --- Additional excess layers, in the blank OTHER rows ---
  additionalExcess.forEach((xs, i) => {
    const row = OTHER_ROWS[i];
    setText(row.description, 'EXCESS LIABILITY');
    setText(row.insurerLetter, rows.letterFor(xs));
    setText(row.policyNumber, xs.PolicyNumber);
    setText(row.effectiveDate, xs.EffectiveDate);
    setText(row.expirationDate, xs.ExpirationDate);

    if (certHolder?.IsAdditionalInsured && (certHolder.AI_LOBs || '').toLowerCase().match(/umb|excess/)) {
      setText(row.additionalInsured, 'Y');
    }
    setText(row.subrogationWaived, subrCode(xs));

    const lines = [
      ['EACH OCCURRENCE', fmtMoney(xs.EachOccurrenceLimit)],
      ['AGGREGATE', fmtMoney(xs.GeneralAggregateLimit)],
    ].filter(([, amount]) => amount);

    lines.forEach(([label, amount], li) => {
      const slot = row.limits[li];
      if (!slot) return;
      setTextFitted(slot.code, label);
      setTextFitted(slot.amount, amount);
    });
  });

  // --- Workers Compensation ---
  if (rows.wc) {
    const wc = rows.wc;
    setText('WorkersCompensationEmployersLiability_InsurerLetterCode_A', rows.letterFor(wc));
    setCheck('WorkersCompensationEmployersLiability_WorkersCompensationStatutoryLimitIndicator_A');

    setText('Policy_WorkersCompensationAndEmployersLiability_PolicyNumberIdentifier_A', wc.PolicyNumber);
    setText('Policy_WorkersCompensationAndEmployersLiability_EffectiveDate_A', wc.EffectiveDate);
    setText('Policy_WorkersCompensationAndEmployersLiability_ExpirationDate_A', wc.ExpirationDate);

    setText('Policy_WorkersCompensation_SubrogationWaivedCode_A', subrCode(wc));

    // ANY PROPRIETOR/PARTNER/EXECUTIVE OFFICER/MEMBER EXCLUDED? (a Y/N text
    // field on this form, not a checkbox).
    const officerExclusion = deriveOfficerExclusion(officers);
    if (officerExclusion.value) {
      setText('WorkersCompensationEmployersLiability_AnyPersonsExcludedIndicator_A', officerExclusion.value);
    } else {
      warnings.push(
        `WC policy ${wc.PolicyNumber}: officer exclusion left blank — ${officerExclusion.reason}.`
      );
    }

    setText('WorkersCompensationEmployersLiability_EmployersLiability_EachAccidentLimitAmount_A', fmtMoney(wc.ELEachAccident));
    setText('WorkersCompensationEmployersLiability_EmployersLiability_DiseaseEachEmployeeLimitAmount_A', fmtMoney(wc.ELDiseaseEachEmployee));
    setText('WorkersCompensationEmployersLiability_EmployersLiability_DiseasePolicyLimitAmount_A', fmtMoney(wc.ELDiseasePolicy));
  }

  // --- Description / Remarks ---
  setText('CertificateOfLiabilityInsurance_ACORDForm_RemarkText_A', certHolder?.DescriptionLanguage);

  // --- Certificate Holder ---
  setText('CertificateHolder_FullName_A', certHolder?.FullName);
  setText('CertificateHolder_MailingAddress_LineOne_A', certHolder?.Address);
  setText('CertificateHolder_MailingAddress_CityName_A', certHolder?.City);
  setText('CertificateHolder_MailingAddress_StateOrProvinceCode_A', certHolder?.State);
  setText('CertificateHolder_MailingAddress_PostalCode_A', certHolder?.Zip);

  // --- Date and signature ---
  setText('Form_CompletionDate_A', today);
  setText('Producer_AuthorizedRepresentative_Signature_A', agency?.ProducerName);

  return { pdfDoc, form, rendered };
}

/**
 * Build the filled certificate. Always a single page.
 *
 * The ACORD 25 has one Umbrella/Excess row, so the lead excess layer goes
 * there and each additional layer is written into a blank OTHER row at the
 * bottom of the coverages table. When the layers outnumber the available
 * rows this throws {@link ExcessCapacityError} rather than truncating —
 * a certificate that silently omits a layer misrepresents coverage.
 *
 * @returns {Promise<{bytes: Uint8Array, rows: Object, warnings: string[], rendered: Object}>}
 * @throws {ExcessCapacityError}
 */
export async function buildACORD25({ pdfBytes, insured, agency, certHolder, policies, officers, today }) {
  const rows = selectCertificateRows(policies);

  const additional = rows.excess.slice(1);
  if (additional.length > OTHER_ROW_CAPACITY) {
    throw new ExcessCapacityError(additional.slice(OTHER_ROW_CAPACITY));
  }

  const warnings = [];
  const ctx = { insured, agency, certHolder, rows, officers, today, warnings };

  const { pdfDoc, rendered } = await fillCertificate(pdfBytes, ctx);
  const bytes = await pdfDoc.save({ updateFieldAppearances: false });

  return { bytes, rows, warnings, rendered };
}

/**
 * Generate and download a filled ACORD 25 Certificate of Liability Insurance.
 * @param {Object} params
 * @param {Object} params.insured - Insured record (CompanyName, address, etc.)
 * @param {Object} params.agency - Agency record (AgencyName, address, contact, etc.)
 * @param {Object} params.certHolder - { FullName, Address, City, State, Zip,
 *   IsAdditionalInsured, AI_LOBs, DescriptionLanguage }
 * @param {Array}  params.policies - Array of policy records
 */
export default async function generateACORD25({ insured, agency, certHolder, policies, officers }) {
  const res = await fetch(BLANK_PDF_URL);
  if (!res.ok) throw new Error(`Failed to load blank ACORD 25 (${res.status})`);
  const pdfBytes = await res.arrayBuffer();

  const now = new Date();
  const today = `${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getDate()).padStart(2, '0')}/${now.getFullYear()}`;

  const { bytes, rows, warnings } = await buildACORD25({
    pdfBytes, insured, agency, certHolder, policies, officers, today,
  });

  console.log('[ACORD 25] Insurer panel:', rows.carriers.map(c => `${c.letter}: ${c.name} (${c.naic})`));
  warnings.forEach(w => console.warn('[ACORD 25]', w));

  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ACORD_25_${(insured?.CompanyName || 'insured').replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
